// src/pages/News/PostDetail.js
import React from 'react';
import { useParams } from 'react-router-dom';
import { Layout, Typography, Card, Row, Col, Button, Divider } from 'antd';
import { Link } from 'react-router-dom';
import { FaTwitter, FaFacebookF, FaLinkedinIn, FaWhatsapp, FaEnvelope } from 'react-icons/fa';

const { Content } = Layout;
const { Title, Paragraph } = Typography;

// Reuse blogPosts from NewsPage.js (ideally, this should come from an API or shared state)
const blogPosts = [
  { id: 1, title: "AI Revolution in Africa", date: "2025-02-10", description: "How AI is transforming industries across Africa...", tags: ["Artificial Intelligence"], image: "https://www.jecthub.com/images/ai-ml.jpeg", views: 1200 },
  { id: 2, title: "Future of Cybersecurity", date: "2025-01-28", description: "Key trends and innovations in cybersecurity for 2025...", tags: ["Cybersecurity"], image: "https://www.jecthub.com/static/media/slider1.0cced2a20e757b6027db.jpg", views: 950 },
  { id: 3, title: "Big Data and You", date: "2024-12-15", description: "Understanding the impact of big data in decision-making...", tags: ["Data Science"], image: "https://www.jecthub.com/images/data-science.jpeg", views: 870 },
  { id: 4, title: "Machine Learning in Healthcare", date: "2024-11-20", description: "AI-driven innovations in healthcare and diagnostics...", tags: ["Artificial Intelligence"], image: "https://www.jecthub.com/images/ai-ml.jpeg", views: 1350 },
  { id: 6, title: "AI for Social Good", date: "2025-02-18", description: "How AI is being used for social impact and solving global challenges...", tags: ["Artificial Intelligence"], image: "https://www.jecthub.com/images/ai-ml.jpeg", views: 1300 },
  { id: 7, title: "The Role of AI in Climate Change", date: "2025-02-05", description: "Exploring how artificial intelligence is helping fight climate change...", tags: ["Artificial Intelligence", "Environment"], image: "https://www.jecthub.com/images/ai-ml.jpeg", views: 1050 },
  { id: 13, title: "The Rise of Edge AI", date: "2025-03-02", description: "How edge AI is revolutionizing real-time computing and IoT devices...", tags: ["Artificial Intelligence", "IoT"], image: "https://www.jecthub.com/images/ai-ml.jpeg", views: 1250 },
];

// Dummy sections for dynamic posts (replace with real content in a production API)
const generateSections = (post) => {
  switch (post.id) {
    case 1: // AI Revolution in Africa
      return [
        { title: 'AI in Healthcare', description: 'AI-powered diagnostic tools are enhancing disease detection and patient care.' },
        { title: 'AI in Agriculture', description: 'AI is helping African farmers increase productivity through smart farming techniques.' },
        { title: 'AI in Education', description: 'AI-powered tutoring systems are personalizing education for students across the continent.' },
        { title: 'AI in Financial Services', description: 'Fintech companies in Africa are leveraging AI to enhance financial inclusion.' },
        { title: 'AI in Smart Cities & Governance', description: 'Governments are adopting AI to improve urban planning and public safety.' },
      ];
    case 2: // Future of Cybersecurity
      return [
        { title: 'Emerging Threats', description: 'New cybersecurity threats are evolving with technology advancements.' },
        { title: 'AI Defenses', description: 'AI is being used to detect and mitigate cyber attacks in real-time.' },
        { title: 'Industry Impact', description: 'Cybersecurity innovations are protecting businesses across sectors.' },
      ];
    case 3: // Big Data and You
      return [
        { title: 'Data-Driven Decisions', description: 'Big data helps individuals and businesses make informed choices.' },
        { title: 'Privacy Concerns', description: 'The rise of big data brings challenges in protecting personal information.' },
      ];
    default:
      return [{ title: 'Overview', description: post.description }]; // Fallback for other posts
  }
};

const PostDetail = () => {
  const { category, slug } = useParams();

  // Find the post matching the category and slug
  const post = blogPosts.find(
    (p) =>
      p.tags[0] === category &&
      p.title.toLowerCase().replace(/\s+/g, '-') === slug
  );

  if (!post) {
    return (
      <Layout>
        <Content style={{ padding: '50px', backgroundColor: '#f0f2f5' }}>
          <Row justify="center">
            <Col span={20}>
              <Title level={2}>Post Not Found</Title>
              <Paragraph>The requested post could not be found.</Paragraph>
            </Col>
          </Row>
        </Content>
      </Layout>
    );
  }

  // Generate sections dynamically
  const sections = generateSections(post);

  // Filter related posts (same tags as the current post, excluding itself)
  const relatedPosts = blogPosts
    .filter((p) => p.id !== post.id && p.tags.some((tag) => post.tags.includes(tag)))
    .slice(0, 4); // Limit to 4 related posts

  // Share functionality
  const pageUrl = window.location.href;
  const pageTitle = post.title;
  const shareLinks = {
    twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`,
    linkedin: `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(pageUrl)}&title=${encodeURIComponent(pageTitle)}`,
    whatsapp: `https://api.whatsapp.com/send?text=${encodeURIComponent(`${pageTitle} - ${pageUrl}`)}`,
    email: `mailto:?subject=${encodeURIComponent(pageTitle)}&body=${encodeURIComponent(`Check out this article: ${pageUrl}`)}`,
  };

  return (
    <Layout>
      <Content style={{ padding: '50px', backgroundColor: '#f0f2f5' }}>
        {/* Header Section */}
        <Row justify="center">
          <Col span={24} style={{ textAlign: 'center' }}>
            <Title level={2} style={{ color: '#1890ff', marginBottom: '10px' }}>
              {post.title}
            </Title>
            <Paragraph style={{ fontSize: '16px', color: '#666' }}>
              <strong>Published on {post.date}</strong>
            </Paragraph>
          </Col>
        </Row>

        {/* Share Buttons */}
        <Row justify="center" style={{ marginBottom: '20px' }}>
          <Col>
            <Button
              type="link"
              href={shareLinks.twitter}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#1DA1F2', padding: '0 10px' }}
            >
              <FaTwitter size={20} />
            </Button>
            <Button
              type="link"
              href={shareLinks.facebook}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#3B5998', padding: '0 10px' }}
            >
              <FaFacebookF size={20} />
            </Button>
            <Button
              type="link"
              href={shareLinks.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#0077B5', padding: '0 10px' }}
            >
              <FaLinkedinIn size={20} />
            </Button>
            <Button
              type="link"
              href={shareLinks.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#25D366', padding: '0 10px' }}
            >
              <FaWhatsapp size={20} />
            </Button>
            <Button
              type="link"
              href={shareLinks.email}
              style={{ color: '#D44638', padding: '0 10px' }}
            >
              <FaEnvelope size={20} />
            </Button>
          </Col>
        </Row>

        {/* Hero Image */}
        <Row justify="center">
          <Col span={20}>
            <img
              src={post.image}
              alt={post.title}
              style={{ width: '100%', height: '400px', objectFit: 'cover', borderRadius: '8px', marginBottom: '20px' }}
            />
          </Col>
        </Row>

        {/* Introduction */}
        <Row justify="center">
          <Col span={20}>
            <Paragraph style={{ fontSize: '16px', lineHeight: '1.6', marginBottom: '30px' }}>
              {post.description} This article explores the transformative impact of {post.tags[0]} across various sectors, highlighting key innovations and future potential.
            </Paragraph>
          </Col>
        </Row>

        {/* Main Sections */}
        <Row gutter={[16, 16]} justify="center">
          {sections.map((section, index) => (
            <Col xs={24} sm={12} md={12} lg={8} key={index}>
              <Card
                title={<Title level={4} style={{ color: '#333' }}>{section.title}</Title>}
                bordered={false}
                style={{ borderRadius: '8px', boxShadow: '0 4px 8px rgba(0,0,0,0.1)' }}
              >
                <Paragraph style={{ color: '#666', fontSize: '14px' }}>
                  {section.description}
                </Paragraph>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Future Outlook */}
        <Row justify="center" style={{ marginTop: '40px' }}>
          <Col span={20}>
            <Title level={3} style={{ color: '#1890ff' }}>
              The Future of {post.tags[0]}
            </Title>
            <Paragraph style={{ fontSize: '16px', lineHeight: '1.6' }}>
              As {post.tags[0]} continues to evolve, its adoption will drive innovation and address critical challenges. Collaboration and investment in technology will shape its trajectory.
            </Paragraph>
            <Paragraph style={{ fontSize: '16px', lineHeight: '1.6' }}>
              Stay tuned with JectHub for more insights on {post.tags[0]} and emerging technologies.
            </Paragraph>
            <Paragraph style={{ fontSize: '14px', fontStyle: 'italic', color: '#888' }}>
              Published by JectHub Tech Insights
            </Paragraph>
          </Col>
        </Row>

        {/* Related News Section */}
        {relatedPosts.length > 0 && (
          <Row justify="center" style={{ marginTop: '40px' }}>
            <Col span={24}>
              <Title level={3} style={{ color: '#1890ff', marginBottom: '20px' }}>
                Related News
              </Title>
              <Row gutter={[16, 16]}>
                {relatedPosts.map((related) => (
                  <Col xs={24} sm={12} md={6} key={related.id}>
                    <Card
                      hoverable
                      cover={<img alt={related.title} src={related.image} style={{ height: '120px', objectFit: 'cover' }} />}
                      style={{ borderRadius: '8px', overflow: 'hidden', boxShadow: '0 4px 8px rgba(0,0,0,0.1)' }}
                    >
                      <Title level={5} style={{ marginBottom: '5px', color: '#333' }}>
                        {related.title}
                      </Title>
                      <Paragraph type="secondary" style={{ fontSize: '12px' }}>
                        {related.date}
                      </Paragraph>
                      <Paragraph style={{ color: '#666', fontSize: '13px' }}>
                        {related.description}{' '}
                        <Link to={`/pages/News/${related.tags[0]}/${related.title.toLowerCase().replace(/\s+/g, '-')}`}>
                          Read more
                        </Link>
                      </Paragraph>
                    </Card>
                  </Col>
                ))}
              </Row>
            </Col>
          </Row>
        )}
      </Content>
    </Layout>
  );
};

export default PostDetail;